import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, LineChart, Archive, CloudSun as CloudIcon, MapPinned } from "lucide-react";
import WeatherHero from "@/components/WeatherHero";
import DashboardGrid from "@/components/DashboardGrid";
import SectionHeader from "@/components/SectionHeader";
import { useCurrent } from "@/hooks/useWeather";
import { formatTime } from "@/lib/weatherUtils";

export default function Home() {
  const { data, isLoading } = useCurrent();

  return (
    <div data-testid="page-home">
      <WeatherHero data={data} loading={isLoading} />

      {/* Dashboard preview */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <SectionHeader
              eyebrow="Dati in tempo reale"
              title="Il polso atmosferico della mia stazione."
              subtitle="Tredici parametri della mia stazione, aggiornati automaticamente ogni 60 secondi direttamente dal sensore installato in Via Loreto."
            />
            {data && (
              <p className="text-sm text-slate-500" data-testid="last-update">
                Ultimo aggiornamento · <span className="text-[#0B132B] font-medium">{formatTime(data.ts)}</span>
              </p>
            )}
          </div>
          <DashboardGrid data={data} />
        </div>
      </section>

      {/* Feature grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            eyebrow="Esplora"
            title="Tutto quello che raccoglie la mia stazione."
            subtitle="Grafici, storico, informazioni sulla strumentazione e sulla posizione: ogni sezione del sito è dedicata unicamente alla mia postazione di Via Loreto."
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.to}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="metric-card p-8 group"
              >
                <span className="h-11 w-11 rounded-full flex items-center justify-center bg-[#0B132B] text-[#FBBF24]">
                  <f.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-2xl text-[#0B132B]">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                <Link to={f.to} className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#0EA5E9] group-hover:gap-2 transition-all">
                  Scopri <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-[#0B132B] p-12 md:p-16 text-white grain">
            <div className="relative z-10 grid lg:grid-cols-5 gap-10 items-center">
              <div className="lg:col-span-3">
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#FBBF24]">Sotto il cofano</p>
                <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight">
                  Una stazione, tanti sensori, dati grezzi.
                </h2>
                <p className="mt-4 text-white/70 text-base md:text-lg">
                  Il sensore installato in Via Loreto trasmette ogni 60 secondi al mio Raspberry Pi che
                  registra le misure con WeeWX. Da lì i dati vengono spediti a questo portale — senza filtri, senza
                  interpolazioni: quello che vedi è esattamente quello che il termometro sta leggendo in questo momento.
                </p>
              </div>
              <div className="lg:col-span-2 grid grid-cols-2 gap-4">
                <TechStat label="Aggiornamento" value="60 s" />
                <TechStat label="Altitudine" value="404 m" />
                <TechStat label="Sensori attivi" value="13" />
                <TechStat label="Uptime" value="24/7" />
              </div>
            </div>
            <div className="absolute -right-32 -bottom-32 h-96 w-96 rounded-full bg-[#0EA5E9]/25 blur-3xl" />
          </div>
        </div>
      </section>
    </div>
  );
}

function TechStat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
      <p className="text-[10px] uppercase tracking-[0.24em] text-white/60">{label}</p>
      <p className="mt-2 font-display text-3xl md:text-4xl text-white font-light">{value}</p>
    </div>
  );
}

const FEATURES = [
  { to: "/dashboard", icon: CloudIcon, title: "Dashboard", desc: "Panoramica live di tutti i sensori con indicatori e stato aggiornato al minuto." },
  { to: "/grafici", icon: LineChart, title: "Grafici", desc: "Serie storiche su 24 ore, 7 giorni, 30 giorni e 12 mesi con visualizzazioni eleganti." },
  { to: "/storico", icon: Archive, title: "Storico", desc: "Archivio completo, ricerca per data e record assoluti della stazione." },
  { to: "/stazione", icon: MapPinned, title: "La Stazione", desc: "Posizione, coordinate, strumentazione e galleria fotografica del presidio." },
];
